import { Crown } from "lucide-react";
import * as stylex from "@stylexjs/stylex";
import "@/colors.stylex";
import { colors } from "@/colors.stylex";
import { MOKA_ADVANTAGE_BAR_TRANSITION_DURATION_MS, MOKA_MATCH_MINIMUM_WIN_PROBABILITY, MOKA_MATCH_PERCENT_MULTIPLIER } from "@/constants";
const _temp = {
  krVfgx: "x1nqzi6q",
  kIyJzY: "x22j8wx",
  "$$css": true
};
const _temp2 = {
  kZKoxP: "x16ye13r",
  kIyJzY: "x22j8wx",
  "$$css": true
};
const styles = {
  ghost: {
    kfzvcC: "x47corl",
    kVAEAm: "x10l6tqk",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    kY2c9j: "x1n327nk",
    kZKoxP: "xjm9jq1",
    kIY38u: "x3sa99s",
    kWkggS: "x1u23wyf",
    k9M9Na: "x6176g0",
    kGVxlE: "x93j36t",
    k1ekBW: "xgkp4zd x4wkmsb",
    kAMwcw: "x4ognrn",
    $$css: true
  },
  ghostPosition: (bottomPercent, transitionDurationMs) => [_temp, {
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${bottomPercent}%`),
    "--x-transitionDuration": (val => typeof val === "number" ? val + "ms" : val != null ? val : undefined)(`${transitionDurationMs}ms`)
  }],
  fill: {
    kVAEAm: "x10l6tqk",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    krVfgx: "x1ey2m1c",
    kWkggS: "xy3d8mg",
    k1ekBW: "xua3uq3 x4wkmsb",
    kAMwcw: "x4ognrn",
    $$css: true
  },
  fillHeight: (heightPercent, transitionDurationMs) => [_temp2, {
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${heightPercent}%`),
    "--x-transitionDuration": (val => typeof val === "number" ? val + "ms" : val != null ? val : undefined)(`${transitionDurationMs}ms`)
  }],
  compactMeter: {
    kVAEAm: "x1n2onr6",
    kZKoxP: "x5yr21d",
    kzqmXN: "xrkia0p",
    kVQacm: "xb3r6kr",
    kaIpWk: "xtkphhm",
    kWkggS: "x1tq242v",
    kGVxlE: "xqud118",
    $$css: true
  },
  defaultMeter: {
    kVAEAm: "x1n2onr6",
    kAzted: "x2lwn1j",
    kzqmXN: "xh8yej3",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "x1t1x2f9",
    kVQacm: "xb3r6kr",
    kaIpWk: "xtkphhm",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1335ttx",
    kWkggS: "xp2616n",
    $$css: true
  }
};
const MokaAdvantageMeter = ({
  blackWinProbability,
  children,
  predictedBlackWinProbability,
  styles: styleOverrides
}) => {
  const blackWinPercentage = Math.round(blackWinProbability * MOKA_MATCH_PERCENT_MULTIPLIER);
  const whiteWinPercentage = MOKA_MATCH_PERCENT_MULTIPLIER - blackWinPercentage;
  const valueText = `Black ${blackWinPercentage}%, White ${whiteWinPercentage}%`;
  return <div aria-label="Moka win probability" aria-valuemax={MOKA_MATCH_PERCENT_MULTIPLIER} aria-valuemin={MOKA_MATCH_MINIMUM_WIN_PROBABILITY} aria-valuenow={blackWinPercentage} aria-valuetext={valueText} role="meter" title={valueText} {...stylex.props(styleOverrides)}>
      <span aria-hidden="true" {...stylex.props(styles.fill, styles.fillHeight(blackWinProbability * MOKA_MATCH_PERCENT_MULTIPLIER, MOKA_ADVANTAGE_BAR_TRANSITION_DURATION_MS))} />
      {predictedBlackWinProbability !== null && <span aria-hidden="true" data-moka-advantage-ghost="" {...stylex.props(styles.ghost, styles.ghostPosition(predictedBlackWinProbability * MOKA_MATCH_PERCENT_MULTIPLIER, MOKA_ADVANTAGE_BAR_TRANSITION_DURATION_MS))} />}
      {children}
    </div>;
};
MokaAdvantageMeter.displayName = "MokaAdvantageMeter";
const MokaAdvantagePlayerLabel = ({
  color,
  winner
}) => <span className="x78zum5 x6s0dn4 x1enigpx">
    {color === "black" ? "B" : "W"}
    {winner === color && <Crown aria-label={`${color === "black" ? "Black" : "White"} won`} className="x1jw3ynk xvle69y x117rol3" />}
  </span>;
MokaAdvantagePlayerLabel.displayName = "MokaAdvantagePlayerLabel";
const MokaAdvantageBar = ({
  blackWinProbability,
  predictedBlackWinProbability = null,
  variant = "default",
  winner = null
}) => {
  if (variant === "compact") {
    return <div className="x10l6tqk xsv2zaq x1mgtmex x6fl0gr x1n327nk">
        <MokaAdvantageMeter blackWinProbability={blackWinProbability} predictedBlackWinProbability={predictedBlackWinProbability} styles={styles.compactMeter} />
        {winner && <Crown aria-label={`${winner === "black" ? "Black" : "White"} won`} {...{
        0: {
          className: "x10l6tqk x1lr0wgl x1jw3ynk xvle69y x1v2qyfo x13vifvy xt44b0r x6ikql9"
        },
        1: {
          className: "x10l6tqk x1lr0wgl x1jw3ynk xvle69y x1v2qyfo x1ey2m1c x1q7mkap x1e0d2z"
        }
      }[!!(winner === "black") << 0]} />}
      </div>;
  }
  const blackWinPercentage = Math.round(blackWinProbability * MOKA_MATCH_PERCENT_MULTIPLIER);
  return <div className="x78zum5 x5yr21d xp8d6y2 x4ked14 x2lah0s xdt5ytf x6s0dn4 xvh977a xmotr4s xboafo0 x1en42ez xo1l8bm xncyony">
      <MokaAdvantagePlayerLabel color="white" winner={winner} />
      <MokaAdvantageMeter blackWinProbability={blackWinProbability} predictedBlackWinProbability={predictedBlackWinProbability} styles={styles.defaultMeter}>
        <span aria-hidden="true" className="x10l6tqk xu96u03 x3m8u43 x2b8uid xss6m8b x6ikql9 x6176g0 x1nh8u4m">
          {MOKA_MATCH_PERCENT_MULTIPLIER - blackWinPercentage}
        </span>
        <span aria-hidden="true" className="x10l6tqk xu96u03 x3m8u43 x2b8uid xss6m8b x6ikql9 x6176g0 x1of4z1n">
          {blackWinPercentage}
        </span>
      </MokaAdvantageMeter>
      <MokaAdvantagePlayerLabel color="black" winner={winner} />
    </div>;
};
MokaAdvantageBar.displayName = "MokaAdvantageBar";
export { MokaAdvantageBar };